<script setup lang="ts">
import {computed} from "vue";
import {ClipboardList} from "@lucide/vue";
import {FollowUpGirerdQuestion, FollowUpGirerdView} from "@/medication-checkup/interfaces/follow-up-girerd";
import {CARD_SHEET, FLAGGED} from "@/medication-checkup/services/SceneClasses";
import {GIRERD_READINGS, girerdReadingTitle} from "@/medication-checkup/services/GirerdReadings";
import MedicationCheckupYesNo from "@/medication-checkup/components/MedicationCheckupYesNo.vue";

/**
 * The Girerd card of the adherence follow-up: the six yes/no questions and
 * — once the block is validated — the reading of the score.
 *
 * Answering is local to the scene: the card shows the answers it is handed
 * while live, the ones the server froze once the block is completed.
 */

const props = defineProps<{
    view: FollowUpGirerdView;
    frozen?: boolean;
    /**
     * The answers given so far by question ref, while live.
     */
    answers?: Record<string, boolean>;
    /**
     * The refs of the questions the scene points at as unanswered.
     */
    flagged?: string[];
}>();

const emit = defineEmits<{
    answer: [ref: string, given: boolean];
}>();

const reading = computed(() => {
    return props.view.interpretation ? GIRERD_READINGS[props.view.interpretation.key] : null;
});

const readingLabel = computed(() => girerdReadingTitle(props.view.interpretation));

const answerOf = (question: FollowUpGirerdQuestion): boolean | null => {
    return props.frozen ? question.answer : props.answers?.[question.ref] ?? null;
};
</script>

<template>
  <div
    :class="CARD_SHEET"
    class="max-w-[640px] rounded-xl overflow-hidden"
  >
    <div class="flex items-center gap-2 px-4 py-3">
      <ClipboardList class="w-[18px] h-[18px] text-primary" />
      <span class="text-sm font-semibold text-primary">Questionnaire d'observance de Girerd</span>
      <span class="text-xs text-muted-foreground">— 6 questions, oui / non</span>
    </div>

    <div
      v-for="(question, index) in view.questions"
      :key="question.ref"
      :class="{[FLAGGED]: flagged?.includes(question.ref)}"
      :data-question-ref="question.ref"
      class="flex items-center gap-2.5 px-4 py-2 border-t border-gray-light animate-message-in"
      :style="{animationDelay: `${index * 60}ms`}"
    >
      <span
        class="w-[22px] h-[22px] shrink-0 rounded-full bg-primary-lightest text-primary text-xs font-bold flex items-center justify-center"
      >
        {{ index + 1 }}
      </span>

      <span class="flex-1 text-sm leading-snug">{{ question.text }}</span>

      <MedicationCheckupYesNo
        :answer="answerOf(question)"
        :disabled="frozen"
        @answer="emit('answer', question.ref, $event)"
      />
    </div>

    <div
      v-if="reading && view.interpretation"
      class="flex items-center gap-3 px-4 py-3.5 border-t border-gray-light animate-message-in"
      :class="reading.classes"
    >
      <span class="w-9 h-9 shrink-0 rounded-full bg-white flex items-center justify-center">
        <component :is="reading.icon" class="w-5 h-5" />
      </span>
      <span class="flex flex-col">
        <span class="text-base font-bold">{{ readingLabel }}</span>
        <span class="text-sm opacity-80">Score de Girerd : {{ view.score }} oui / 6</span>
      </span>
    </div>
  </div>
</template>
